// ============================================================
// CharacterPreview - Compact character summary card for hover
// tooltips in the Character Library.
// ============================================================
import type { Character, CharacteristicId } from '../engine/character/types';
import { talentText, traitText } from '../engine/character/instanceText';
import styles from '../styles/CharacterLibrary.module.css';
import { previewPlacement } from '../utils/tooltipPlacement';
import type { Rect } from '../utils/tooltipPlacement';

/* Its own CSS caps it at 280 wide; the height is a fair reading of a
   full card and only decides when it flips away from an edge, so being
   a little out costs nothing. */
const PREVIEW_WIDTH = 280;
const PREVIEW_HEIGHT = 320;

const CHAR_ORDER: { id: CharacteristicId; label: string }[] = [
  { id: 'ws', label: 'WS' },
  { id: 'bs', label: 'BS' },
  { id: 'str', label: 'S' },
  { id: 'tgh', label: 'T' },
  { id: 'agi', label: 'Ag' },
  { id: 'int', label: 'Int' },
  { id: 'per', label: 'Per' },
  { id: 'wp', label: 'WP' },
  { id: 'fel', label: 'Fel' },
];

interface Props {
  character: Character;
  /** The row's viewport rect. Without it the preview falls back to the stylesheet. */
  anchor?: Rect;
}

/** A titled row of chips: "Weapons (2):" and a chip per name. */
function ChipSection({ title, items }: { title: string; items: { key: string; label: string }[] }) {
  if (items.length === 0) return null;
  return (
    <div className={styles.previewSection}>
      <span className={styles.previewDim}>
        {title} ({items.length}):
      </span>
      <div className={styles.previewList}>
        {items.map((item) => (
          <span key={item.key} className={styles.previewChip}>
            {item.label}
          </span>
        ))}
      </div>
    </div>
  );
}

function CharacteristicsGrid({ characteristics }: { characteristics: Character['characteristics'] }) {
  return (
    <div className={styles.previewChars}>
      {CHAR_ORDER.map(({ id, label }) => (
        <div key={id} className={styles.previewCharCell}>
          <span className={styles.previewCharLabel}>{label}</span>
          {/* A CARD MUST NOT TAKE THE APP DOWN. An imported character
              can be missing a characteristic - this reads `str`/`tgh`/
              `agi` and a file may say `s`/`t`/`ag` - and this line
              threw `undefined.value` from inside a HOVER, which the
              ErrorBoundary turned into "Something went wrong" for the
              whole session. A preview shows what it has (v8.26.0). */}
          <span className={styles.previewCharValue}>{characteristics?.[id]?.value ?? '-'}</span>
        </div>
      ))}
    </div>
  );
}

function WoundsRow({ wounds, horde }: { wounds: Character['wounds']; horde?: Character['horde'] }) {
  return (
    <div className={styles.previewRow}>
      <span className={styles.previewDim}>Wounds:</span>
      <span>{wounds.max}</span>
      {horde?.isHorde && (
        <>
          <span className={styles.previewDim} style={{ marginLeft: 8 }}>
            Horde:
          </span>
          <span>{horde.maxMagnitude}</span>
        </>
      )}
    </div>
  );
}

/**
 * FIXED, off the row's measured rect. See `tooltipPlacement` - as an
 * absolutely positioned child it counted towards the panel's scrollable
 * overflow, and hovering a lone character made the whole menu tremble
 * (v8.23.1).
 */
function placement(anchor: Rect | undefined) {
  if (!anchor || typeof window === 'undefined') return null;
  return previewPlacement(
    anchor,
    { width: PREVIEW_WIDTH, height: PREVIEW_HEIGHT },
    { width: window.innerWidth, height: window.innerHeight },
  );
}

export default function CharacterPreview({ character, anchor }: Props) {
  const place = placement(anchor);

  // EVERY FIELD DEFAULTED. This destructured nine things and then read
  // into all of them; a character that is missing any one - which an
  // imported file easily is - threw from inside a hover handler, and the
  // ErrorBoundary replaced the entire session with "Something went
  // wrong". A preview is the least important thing on screen and must be
  // the least able to break anything (v8.26.0).
  const {
    characteristics = {} as Character['characteristics'],
    weapons = [],
    talents = [],
    traits = [],
    wounds = { current: 0, max: 0 },
    armour = {} as Character['armour'],
    horde,
  } = character ?? {};

  return (
    <div
      className={styles.preview}
      role="tooltip"
      aria-label={`Preview of ${character.name}`}
      style={place ? { top: place.top, left: place.left } : undefined}
    >
      <CharacteristicsGrid characteristics={characteristics} />
      <WoundsRow wounds={wounds} horde={horde} />
      {armour.name && (
        <div className={styles.previewRow}>
          <span className={styles.previewDim}>Armour:</span>
          <span>{armour.name}</span>
        </div>
      )}
      <ChipSection title="Weapons" items={weapons.map((w) => ({ key: w.id, label: w.name || 'Unnamed' }))} />
      <ChipSection
        title="Talents"
        items={talents.map((t, i) => ({ key: `${i}-${t.talentId}`, label: talentText(t)?.name || t.talentId }))}
      />
      <ChipSection
        title="Traits"
        items={traits.map((t, i) => ({ key: `${i}-${t.traitId}`, label: traitText(t)?.name || t.traitId }))}
      />
    </div>
  );
}
